<template>
  <TheModal :show="show" size="lg" @close="onClose">
    <h2 class="h5 mb-1">Обратная связь</h2>
    <p class="text-muted small mb-4">
      Расскажите, что улучшить в сервисе, оставьте отзыв или сообщите об ошибке. Можно приложить скриншот экрана.
    </p>

    <form @submit.prevent="submit">
      <div class="mb-3">
        <label class="form-label" for="feedback-kind">Тип сообщения</label>
        <select id="feedback-kind" v-model="kind" class="form-select" required>
          <option value="suggestion">Предложение по развитию</option>
          <option value="review">Отзыв</option>
          <option value="bug">Ошибка / баг</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="form-label" for="feedback-message">Сообщение</label>
        <textarea
          id="feedback-message"
          v-model="message"
          class="form-control"
          rows="5"
          required
          minlength="10"
          maxlength="5000"
          placeholder="Опишите идею, впечатление или проблему…"
        />
        <div class="form-text">Не менее 10 символов.</div>
      </div>

      <div class="mb-3">
        <label class="form-label" for="feedback-screenshot">Скриншот (необязательно)</label>
        <input
          id="feedback-screenshot"
          ref="fileInput"
          type="file"
          class="form-control"
          accept="image/png,image/jpeg,image/webp"
          @change="onFileChange"
        />
        <div class="form-text">PNG, JPEG или WebP, до 5 МБ.</div>
        <p v-if="fileError" class="text-danger small mb-0 mt-1">{{ fileError }}</p>
      </div>

      <div v-if="error" class="alert alert-warning py-2 small">{{ error }}</div>
      <div v-if="success" class="alert alert-success py-2 small">{{ success }}</div>

      <div class="d-flex flex-wrap gap-2 justify-content-end">
        <button type="button" class="btn btn-outline-secondary" :disabled="submitting" @click="onClose">
          Отмена
        </button>
        <button type="submit" class="btn btn-primary" :disabled="submitting || !canSubmit">
          <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
          Отправить
        </button>
      </div>
    </form>
  </TheModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import TheModal from '@/components/TheModal.vue'
import api from '@/api'

const props = defineProps({
  show: { type: Boolean, default: false },
})

const emit = defineEmits(['close'])

const kind = ref('suggestion')
const message = ref('')
const screenshot = ref(null)
const fileError = ref('')
const error = ref('')
const success = ref('')
const submitting = ref(false)
const fileInput = ref(null)

const MAX_BYTES = 5 * 1024 * 1024
const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp'])

const canSubmit = computed(() => message.value.trim().length >= 10 && !fileError.value)

function resetForm() {
  kind.value = 'suggestion'
  message.value = ''
  screenshot.value = null
  fileError.value = ''
  error.value = ''
  success.value = ''
  if (fileInput.value) fileInput.value.value = ''
}

function onFileChange(e) {
  fileError.value = ''
  const file = e.target.files?.[0]
  if (!file) {
    screenshot.value = null
    return
  }
  if (!ALLOWED_TYPES.has(file.type)) {
    fileError.value = 'Допустимы только PNG, JPEG и WebP.'
    screenshot.value = null
    return
  }
  if (file.size > MAX_BYTES) {
    fileError.value = 'Файл больше 5 МБ.'
    screenshot.value = null
    return
  }
  screenshot.value = file
}

function onClose() {
  if (!submitting.value) emit('close')
}

async function submit() {
  if (!canSubmit.value || submitting.value) return
  submitting.value = true
  error.value = ''
  success.value = ''

  const fd = new FormData()
  fd.append('kind', kind.value)
  fd.append('message', message.value.trim())
  fd.append('page_url', window.location.href)
  if (screenshot.value) fd.append('screenshot', screenshot.value)

  try {
    await api.post('/feedback/', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    success.value = 'Спасибо! Мы получили ваше сообщение.'
    setTimeout(() => {
      resetForm()
      emit('close')
    }, 1200)
  } catch (err) {
    const detail = err?.response?.data?.detail
    if (typeof detail === 'string') {
      error.value = detail
    } else if (detail && typeof detail === 'object') {
      error.value = Object.values(detail).flat().join(' ')
    } else {
      error.value = 'Не удалось отправить сообщение. Попробуйте позже.'
    }
  } finally {
    submitting.value = false
  }
}

watch(
  () => props.show,
  (visible) => {
    if (!visible) resetForm()
  },
)
</script>
